import useApiRequest from "@/lib/hooks/use-request";
import { DataTableProps } from "@/lib/interfaces/utils";
import { Button } from "@/ui-components/ui/button";
import ConfirmationDialog from "@/ui-components/ui/confirmation-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui-components/ui/table";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { Clock, Mail, Trash2, User } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

export interface InvitationListResponseDto {
  _id: string;
  firstName: string;
  lastName: string;
  mail: string;
  role: string;
  expiresAt?: string;
  createdAt: string;
}

const InvitationsDataTable = ({
  data,
  isLoading,
  pageCount,
  pagination,
  setPagination,
  setReload,
}: DataTableProps<InvitationListResponseDto>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowSelection, setRowSelection] = useState({});
  const { apiRequest } = useApiRequest();

  const getRoleColor = (role: string) => {
    switch (role.toUpperCase()) {
      case "ADMIN":
        return "bg-red-50 text-red-700 ring-red-700/10";
      case "SUPERADMIN":
        return "bg-yellow-50 text-yellow-700 ring-yellow-700/10";
      default:
        return "bg-blue-50 text-blue-700 ring-blue-700/10";
    }
  };

  const columns: ColumnDef<InvitationListResponseDto>[] = [
    {
      accessorKey: "name",
      header: () => (
        <div className="flex items-center">
          <p>Name</p>
        </div>
      ),
      cell: ({ row }) => {
        const fullName = `${row.original.firstName} ${row.original.lastName}`;
        return (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <p className="font-medium">{fullName}</p>
          </div>
        );
      },
    },
    {
      accessorKey: "mail",
      header: () => (
        <div className="flex items-center">
          <p>Email</p>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4" />
          <span>{row.original.mail}</span>
        </div>
      ),
    },
    {
      accessorKey: "role",
      header: () => (
        <div className="flex items-center">
          <p>Rolle</p>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex">
          <span
            className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${getRoleColor(
              row.original.role
            )}`}
          >
            {row.original.role}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "expiresAt",
      header: () => (
        <div className="flex items-center">
          <p>Läuft ab am</p>
        </div>
      ),
      cell: ({ row }) => {
        if (!row.original.expiresAt) return <span>-</span>;
        return (
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>{format(new Date(row.original.expiresAt), "dd.MM.yyyy HH:mm")}</span>
          </div>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const handleDelete = async () => {
          await apiRequest(`settings/users/invitations/${row.original._id}`, "DELETE", {
            toast: {
              toastText: "Invitation deleted",
            },
          });
          setReload((prev) => !prev);
        };

        return (
          <div className="flex justify-end">
            <ConfirmationDialog
              onConfirm={handleDelete}
              title="Einladung löschen"
              description="Möchten Sie die Einladung wirklich löschen?"
              variant="destructive"
            >
              <Button variant="link" className="hover:text-red-500 text-black">
                <Trash2 className="h-4 w-4" />
              </Button>
            </ConfirmationDialog>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    pageCount,
    manualPagination: true,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    initialState: { pagination: { pageSize: 20 } },
    state: {
      pagination,
      rowSelection,
    },
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="hover:bg-gray-100">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Keine Einladungen gefunden.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between space-x-2 py-4">
        <Button
          className="text-[17px] text-[#888] hover:text-[#12282A]"
          variant="outline"
          size="sm"
          onClick={() => {
            table.previousPage();
            const params = new URLSearchParams(searchParams);
            params.set("page", table.getState().pagination.pageIndex.toString());
            setSearchParams(params);
          }}
          disabled={!table.getCanPreviousPage()}
        >
          Letzte
        </Button>
        <span className="text-sm text-[#D0D5DD]">
          Seite {table.getState().pagination.pageIndex + 1} von {table.getPageCount()}
        </span>
        <Button
          className="text-[17px] text-[#888] hover:text-[#12282A]"
          variant="outline"
          size="sm"
          onClick={() => {
            table.nextPage();
            const params = new URLSearchParams(searchParams);
            params.set("page", (table.getState().pagination.pageIndex + 2).toString());
            setSearchParams(params);
          }}
          disabled={!table.getCanNextPage()}
        >
          Nächste
        </Button>
      </div>
    </div>
  );
};

export default InvitationsDataTable;
